import React, { useMemo } from 'react';
import { observer } from 'src/utils/mobx-react';
import { BadgeBonus } from 'src/domains/common/components/badge/badge';
import { DateTime } from 'src_common/utils/time/time';
import { BonusModel } from 'src/domains/casino/state/AppCasino/store/campaign-store/BonusModel';
import { useAppStateContext } from 'src/AppState/AppState';
import { useCommon } from 'src/AppState/Common';
import { BonusCardState, RewardType } from 'src/components/CustomersBar/CustomerTab/PromotionHistory/BonusCardState';
import { cn } from 'src/domains/common/components/utils';
import { BonusCardButtons } from 'src/components/CustomersBar/CustomerTab/PromotionHistory/bonusCardButtonsHelper';
import { RewardsNavigationState } from 'src/components/CustomersBar/CustomerTab/PromotionHistory/PromotionHistoryTabs/RewardTab/RewardsNavigation.state';

interface BonusCardPropsType {
    bonusModel: BonusModel;
    userId: number;
    type: RewardType;
    state: RewardsNavigationState;
}

export const BonusCard = observer('BonusCard', ({ bonusModel, type, userId, state }: BonusCardPropsType) => {
    const common = useCommon();
    const { models } = useAppStateContext();
    const walletData = models.getUserModel(userId).walletData.data;
    const { expiryAt, rewardName, totalAmmount, campaignModelWhenReady, bonusSource } = bonusModel;
    const dateFormatted = DateTime.from(expiryAt)?.format('DD-MM-YYYY HH:mm');

    const bonusCardState = useMemo(() => new BonusCardState(bonusModel, common), []);
    const { bonusStatus, bonusId, getAmount } = bonusCardState;
    const isActive = bonusId === state.activeModel?.id;

    if (walletData === null) {
        return null;
    }

    return (
        <div
            className={cn('p-3 max-w-sm w-full rounded-md bg-white border border-solid border-border', {
                'border-primary': isActive,
            })}
        >
            <div className='flex flex-col gap-4 justify-between w-full'>
                <div className='flex justify-between items-center w-full'>
                    <div className='text-lg font-bold overflow-hidden text-pretty line-clamp-3'>{rewardName}</div>

                    <BadgeBonus
                        key={bonusId}
                        variant={bonusStatus}
                    />
                </div>
                <div className='flex flex-col gap-2'>
                    {totalAmmount === null ? null : (
                        <div className='flex justify-between  '>
                            <p className='text-xs m-0'>Amount:</p>
                            <p className='text-xs m-0 font-bold'>{getAmount(walletData, totalAmmount, type)}</p>
                        </div>
                    )}
                    <div className='flex justify-between'>
                        <p className='text-xs m-0'>Bonus Source:</p>
                        <p className='text-xs m-0 capitalize'>{bonusSource}</p>
                    </div>

                    {campaignModelWhenReady?.campaignName === undefined ? null : (
                        <div className='flex justify-between space-x-2'>
                            <p className='text-xs m-0 whitespace-nowrap'>Campaign Name:</p>
                            <p
                                title={campaignModelWhenReady.campaignName}
                                className='text-xs m-0 line-clamp-1 text-ellipsis text-nowrap'
                            >
                                {campaignModelWhenReady.campaignName}
                            </p>
                        </div>
                    )}
                    <div className='flex justify-between'>
                        <p className='text-xs m-0'>Expiration Date:</p>
                        <p className='text-xs m-0 text-right'>{dateFormatted}</p>
                    </div>
                </div>

                <BonusCardButtons
                    model={bonusModel}
                    accountId={userId}
                    bonusCardState={bonusCardState}
                    state={state}
                />
            </div>
        </div>
    );
});
